import { useMemo, useState } from "react";
import { TrendingDown, TrendingUp, FileDown, Minus } from "lucide-react";
import { rs, pct, qty, monthOf, todayIso, daysInMonth, fmtMonth } from "@/lib/format";
import { useReport } from "@/pages/Dashboard";
import { PageHeader, Panel } from "@/components/Kpi";
import { exportComparePdf } from "@/lib/comparePdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRESETS = { h1: "1st – 15th", h2: "16th – month end", month: "Full month" };

const prevMonth = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, "0")}`;
};
const rangeFor = (preset, month) => {
  const dim = daysInMonth(month);
  if (preset === "h1") return [`${month}-01`, `${month}-15`];
  if (preset === "h2") return [`${month}-16`, `${month}-${dim}`];
  return [`${month}-01`, `${month}-${dim}`];
};
const labelFor = (preset, month) => `${PRESETS[preset].replace("month end", "end")} ${fmtMonth(month)}`;
const shortFor = (month) => new Date(`${month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
const change = (a, b) => {
  if (a > 0) return (b - a) / a;
  if (a === 0) return b ? "new" : 0;
  return null;
};

const Delta = ({ v, change: ch, inverse }) => {
  const good = inverse ? v < 0 : v > 0;
  const Icon = v === 0 ? Minus : good ? TrendingUp : TrendingDown;
  const tone = v === 0 ? "text-slate-400" : good ? "text-emerald-700" : "text-rose-700";
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <Icon className="h-3.5 w-3.5" />{ch === "new" ? "new" : ch === null ? "—" : `${ch > 0 ? "+" : ""}${(ch * 100).toFixed(1)}%`}
    </span>
  );
};

const CompareCard = ({ label, a, b, fmt, change: ch, inverse, testId }) => (
  <div className="fade-up rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="num mt-2 text-2xl font-bold tracking-tight text-slate-900" data-testid={`${testId}-value`}>{fmt(b)}</div>
    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
      <span>was {fmt(a)}</span><Delta v={b - a} change={ch} inverse={inverse} />
    </div>
  </div>
);

const Th = ({ children, r }) => <th className={`py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap ${r ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, r, b, className = "" }) => <td className={`py-2 px-3 whitespace-nowrap ${r ? "text-right num" : ""} ${b ? "font-semibold" : ""} ${className}`}>{children}</td>;

const ItemDiffTable = ({ rows, labelA, labelB, testId, tone }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="border-b"><tr>
        <Th>Item</Th><Th>Category</Th><Th r>Qty {labelA}</Th><Th r>Qty {labelB}</Th><Th r>{labelA}</Th><Th r>{labelB}</Th><Th r>Difference</Th><Th r>Change</Th>
      </tr></thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.id} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`${testId}-row-${i.id}`}>
            <Td b>{i.name}</Td><Td className="text-slate-500">{i.category}</Td>
            <Td r>{qty(i.qtyA)}</Td><Td r>{qty(i.qtyB)}</Td><Td r>{rs(i.a)}</Td><Td r>{rs(i.b)}</Td>
            <Td r b className={tone}>{i.diff > 0 ? `+${rs(i.diff)}` : rs(i.diff)}</Td>
            <Td r><Delta v={i.diff} change={i.change} /></Td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={8} className="py-6 text-center text-slate-400">Nothing here for this period.</td></tr>}
      </tbody>
      {rows.length > 0 && (
        <tfoot><tr className="bg-slate-50 font-bold text-slate-800">
          <Td>Total</Td><Td /><Td r>{qty(rows.reduce((s, i) => s + i.qtyA, 0))}</Td><Td r>{qty(rows.reduce((s, i) => s + i.qtyB, 0))}</Td>
          <Td r>{rs(rows.reduce((s, i) => s + i.a, 0))}</Td><Td r>{rs(rows.reduce((s, i) => s + i.b, 0))}</Td>
          <Td r className={tone} data-testid={`${testId}-total`}>{rs(rows.reduce((s, i) => s + i.diff, 0))}</Td><Td />
        </tr></tfoot>
      )}
    </table>
  </div>
);

export default function Compare() {
  const [preset, setPreset] = useState("h1");
  const [monthB, setMonthB] = useState(monthOf(todayIso()));
  const [monthA, setMonthA] = useState(prevMonth(monthOf(todayIso())));

  const [aStart, aEnd] = rangeFor(preset, monthA);
  const [bStart, bEnd] = rangeFor(preset, monthB);
  const A = useReport(aStart, aEnd);
  const B = useReport(bStart, bEnd);
  const labelA = labelFor(preset, monthA), labelB = labelFor(preset, monthB);
  const shortA = shortFor(monthA), shortB = shortFor(monthB);

  const view = useMemo(() => {
    if (!A || !B) return null;
    const mapA = Object.fromEntries(A.items.map((i) => [i.id, i]));
    const rows = B.items.map((i) => {
      const a = mapA[i.id] || { sales: 0, qty: 0 };
      return { id: i.id, name: i.name, category: i.category, a: a.sales, b: i.sales, qtyA: a.qty, qtyB: i.qty,
               diff: i.sales - a.sales, change: change(a.sales, i.sales) };
    }).filter((r) => r.a || r.b);
    const declines = rows.filter((r) => r.diff < 0).sort((x, y) => x.diff - y.diff);
    const growth = rows.filter((r) => r.diff > 0).sort((x, y) => y.diff - x.diff);
    const catMap = {};
    rows.forEach((r) => {
      const c = (catMap[r.category] = catMap[r.category] || { name: r.category, a: 0, b: 0 });
      c.a += r.a; c.b += r.b;
    });
    const cats = Object.values(catMap).map((c) => ({ ...c, diff: c.b - c.a, change: change(c.a, c.b) })).sort((x, y) => x.diff - y.diff);
    const kpis = [
      { key: "gross", label: "Gross sales", a: A.summary.gross, b: B.summary.gross, fmt: rs },
      { key: "plates", label: "Plates served", a: A.summary.plates, b: B.summary.plates, fmt: qty },
      { key: "avg", label: "Avg sales / trading day", a: A.summary.avg_per_day, b: B.summary.avg_per_day, fmt: rs },
      { key: "net", label: "Net sales (after deduction)", a: A.summary.net, b: B.summary.net, fmt: rs },
      { key: "expense", label: "Total expenses", a: A.summary.total_expense, b: B.summary.total_expense, fmt: rs, inverse: true },
      { key: "profit", label: "Net profit", a: A.summary.profit, b: B.summary.profit, fmt: rs },
    ].map(({ key, ...k }) => ({ id: key, ...k, change: change(k.a, k.b) }));
    return { rows, declines, growth, cats, kpis };
  }, [A, B]);

  const drop = view ? view.declines.reduce((s, i) => s + i.diff, 0) : 0;
  const gain = view ? view.growth.reduce((s, i) => s + i.diff, 0) : 0;

  return (
    <div>
      <PageHeader title="Compare Periods" subtitle="Same fortnight, two months — see exactly which items fell behind and which ones to push.">
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-44 bg-white" data-testid="compare-preset-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PRESETS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`compare-preset-${k}`}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="month" value={monthA} onChange={(e) => e.target.value && setMonthA(e.target.value)} className="w-40 bg-white" data-testid="compare-month-a" />
        <span className="text-sm font-semibold text-slate-400">vs</span>
        <Input type="month" value={monthB} onChange={(e) => e.target.value && setMonthB(e.target.value)} className="w-40 bg-white" data-testid="compare-month-b" />
        <Button disabled={!view} onClick={() => exportComparePdf({ business: B?.business_name, labelA: shortA, labelB: shortB, subtitle: `${labelB}   vs   ${labelA}`, ...view })}
          className="bg-slate-900 hover:bg-slate-800" data-testid="compare-export-pdf-button"><FileDown className="h-4 w-4 mr-1" />Export PDF</Button>
      </PageHeader>

      <div className="mb-6 text-sm text-slate-600" data-testid="compare-range-label">
        Comparing <span className="font-semibold text-slate-900">{labelB}</span> against <span className="font-semibold text-slate-900">{labelA}</span>
      </div>

      {!view ? <div className="text-slate-500">Loading…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {view.kpis.map((k) => <CompareCard key={k.id} {...k} testId={`compare-kpi-${k.id}`} />)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-5" data-testid="compare-drop-summary">
              <div className="flex items-center gap-2 text-rose-800"><TrendingDown className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Lost sales</span></div>
              <div className="num mt-2 text-2xl font-bold text-rose-900">{rs(Math.abs(drop))}</div>
              <div className="mt-1 text-xs text-rose-700">across {view.declines.length} items that sold less than {labelA}</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="compare-gain-summary">
              <div className="flex items-center gap-2 text-emerald-800"><TrendingUp className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">Gained sales</span></div>
              <div className="num mt-2 text-2xl font-bold text-emerald-900">{rs(gain)}</div>
              <div className="mt-1 text-xs text-emerald-700">across {view.growth.length} items that grew — push these harder</div>
            </div>
          </div>

          <Panel title={`Sales dropped vs ${labelA}`} testId="compare-declines-panel">
            <ItemDiffTable rows={view.declines} labelA={shortA} labelB={shortB} testId="compare-declines-table" tone="text-rose-700" />
          </Panel>

          <Panel title="Sales grew — target these items" testId="compare-growth-panel">
            <ItemDiffTable rows={view.growth} labelA={shortA} labelB={shortB} testId="compare-growth-table" tone="text-emerald-700" />
          </Panel>

          <Panel title="Category-wise" testId="compare-category-panel">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compare-category-table">
                <thead className="border-b"><tr><Th>Category</Th><Th r>{shortA}</Th><Th r>{shortB}</Th><Th r>Difference</Th><Th r>Change</Th><Th r>Share now</Th></tr></thead>
                <tbody>
                  {view.cats.map((c) => (
                    <tr key={c.name} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`compare-category-row-${c.name}`}>
                      <Td b>{c.name}</Td><Td r>{rs(c.a)}</Td><Td r>{rs(c.b)}</Td>
                      <Td r b className={c.diff < 0 ? "text-rose-700" : "text-emerald-700"}>{c.diff > 0 ? `+${rs(c.diff)}` : rs(c.diff)}</Td>
                      <Td r><Delta v={c.diff} change={c.change} /></Td>
                      <Td r>{pct(B.summary.gross ? c.b / B.summary.gross : 0)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
